import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { GoogleLogin } from '@react-oauth/google';
import { PublicClientApplication } from '@azure/msal-browser';
import { config } from '../config/config';
import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    Paper,
    Divider,
    Alert,
    InputAdornment,
    IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import logo from '../assets/images/privigo-logo-dark.svg';
import axios from 'axios';

// SSO (Google/Microsoft) is hidden for now — no one is using it, and it would
// otherwise require registering the app origin in Google Cloud Console + Azure AD.
// Re-enable later by setting REACT_APP_ENABLE_SSO=true at build time (and adding
// the served origin to the provider allowlists). Handler code below is kept intact.
const ENABLE_SSO = process.env.REACT_APP_ENABLE_SSO === 'true';

const msalInstance = new PublicClientApplication({
    auth: {
        clientId: config.auth.microsoft.clientId,
        authority: config.auth.microsoft.authority,
    },
});

export const Login: React.FC = () => {
    const { isAuthenticated, loading, login, loginWithGoogle, loginWithMicrosoft } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Redirect if already authenticated
    useEffect(() => {
        console.log('Login useEffect - isAuthenticated:', isAuthenticated, 'loading:', loading);
        if (isAuthenticated && !loading) {
            console.log('Navigating to dashboard...');
            navigate('/dashboard');
        }
    }, [isAuthenticated, loading, navigate]);

    // Only show loading screen for initial auth check, not for sign-in process
    if (loading && !isSigningIn) {
        return <div>Loading...</div>;
    }

    const handleTraditionalLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password) {
            setError('Please enter both email and password');
            return;
        }
        
        try {
            setIsSigningIn(true);
            setError(null);
            // Use auth context login method to update state
            await login({ email, password });
            // Navigation will be handled by the useEffect that watches isAuthenticated
        } catch (err) {
            // Show error message
            setError('Invalid email or password');
            console.log('Login failed:', err);
        } finally {
            setIsSigningIn(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse: any) => {
        try {
            setIsSigningIn(true);
            setError(null);
            // Use auth context login method to update state
            await loginWithGoogle(credentialResponse.credential);
            // Navigation will be handled by the useEffect that watches isAuthenticated
        } catch (err) {
            // Show error message
            setError('Google login failed');
            console.log('Google login failed:', err);
        } finally {
            setIsSigningIn(false);
        }
    };

    const handleMicrosoftLogin = async () => {
        try {
            setIsSigningIn(true);
            const response = await msalInstance.loginPopup({
                scopes: ['user.read'],
            });
            setError(null);
            // Use auth context login method to update state
            await loginWithMicrosoft(response.accessToken);
            // Navigation will be handled by the useEffect that watches isAuthenticated
        } catch (err) {
            // Show error message
            setError('Microsoft login failed');
            console.log('Microsoft login failed:', err);
        } finally {
            setIsSigningIn(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Container component="main" maxWidth="xs" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Paper elevation={3} sx={{ padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                        <Box
                            component="img"
                            src={logo}
                            alt="Privigo Logo"
                            sx={{
                                width: '200px',
                                height: 'auto',
                                mb: 3
                            }}
                        />
                        <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
                            Sign in to Privigo
                        </Typography>
                        {error && (
                            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                                {error}
                            </Alert>
                        )}
                        <Box component="form" onSubmit={handleTraditionalLogin} sx={{ mt: 1, width: '100%' }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label="Email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                autoFocus
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                size="small"
                                            >
                                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isSigningIn}
                                sx={{ mt: 3, mb: 2 }}
                            >
                                {isSigningIn ? 'Signing In...' : 'Sign In'}
                            </Button>
                        </Box>
                        {ENABLE_SSO && (
                            <>
                                <Divider sx={{ width: '100%', my: 2 }}>OR</Divider>
                                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <GoogleLogin
                                        onSuccess={handleGoogleSuccess}
                                        onError={() => setError('Google login failed')}
                                        useOneTap
                                    />
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={handleMicrosoftLogin}
                                        disabled={isSigningIn}
                                        sx={{ mt: 1 }}
                                    >
                                        {isSigningIn ? 'Signing In...' : 'Sign in with Microsoft'}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Paper>
                </Box>
            </Container>
        </Box>
    );
}; 